import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMatters, getMatter, startTimer, createTimeEntry, updateTimeEntry, getBillingSettings, type TimeEntry } from '../api/client';
import { parseDurationToMinutes } from '../utils/parseDuration';
import { billableSummary, formatMinutesAsHm } from '../utils/billingRounding';
import { X, Search } from 'lucide-react';
import Dialog from './Dialog';
import { useIsAdmin } from '../hooks/useCurrentUser';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const ACTIVITY_CATEGORIES = [
  'Research',
  'Drafting',
  'Court Appearance',
  'Client Communication',
  'Case Review',
  'Document Review',
  'Administrative',
  'Travel',
  'Other',
];

type MatterOption = {
  id: number;
  title: string;
  contact_name?: string | null;
};

interface TimerModalProps {
  mode: 'start' | 'edit' | 'manual';
  entry?: TimeEntry;
  defaultMatterId?: number;
  onClose: () => void;
  onSuccess: (entry: TimeEntry) => void;
}

export default function TimerModal({ mode, entry, defaultMatterId, onClose, onSuccess }: TimerModalProps) {
  const isAdmin = useIsAdmin();
  const [matterSearch, setMatterSearch] = useState('');
  const debouncedSearch = useDebounce(matterSearch, 300);
  const [showDropdown, setShowDropdown] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [matterId, setMatterId] = useState<number | null>(entry?.matter_id ?? defaultMatterId ?? null);
  const [selectedMatterInfo, setSelectedMatterInfo] = useState<{ id: number; title: string } | null>(null);
  const [description, setDescription] = useState(entry?.description || '');
  const [category, setCategory] = useState(entry?.activity_category || '');
  const [billable, setBillable] = useState(entry?.billable ?? true);
  const [durationText, setDurationText] = useState<string>(() => {
    if (entry?.duration_seconds) return formatMinutesAsHm(Math.round(entry.duration_seconds / 60));
    return '';
  });
  const [entryDate, setEntryDate] = useState(() => {
    if (entry?.entry_date) return entry.entry_date;
    return new Date().toISOString().split('T')[0];
  });
  const [rateCents, setRateCents] = useState<number | ''>(entry?.rate_cents ?? '');

  const { data: mattersData } = useQuery({
    queryKey: ['timer-matters', { search: debouncedSearch, limit: 20 }],
    queryFn: () => getMatters({ search: debouncedSearch || undefined, limit: 20 }),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30000,
  });

  const matters: MatterOption[] = debouncedSearch.length >= 2 ? (mattersData?.items ?? []) : [];

  // Look up the initially-selected matter title (for defaultMatterId / edit mode)
  const {
    data: initialMatter,
    isLoading: isMatterLoading,
    isError: isMatterError,
    refetch: refetchMatter,
  } = useQuery({
    queryKey: ['timer-matter-lookup', matterId],
    queryFn: () => getMatter(matterId!),
    enabled: matterId !== null && selectedMatterInfo === null,
    staleTime: Infinity,
  });

  const startMutation = useMutation({
    mutationFn: startTimer,
    onSuccess: (data) => onSuccess(data),
  });

  const createMutation = useMutation({
    mutationFn: createTimeEntry,
    onSuccess: (data) => onSuccess(data),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateTimeEntry>[1]) =>
      updateTimeEntry(entry!.id, data),
    onSuccess: (data) => onSuccess(data),
  });

  // Firm-wide billable increment (#1859), so the field can show what a typed
  // duration will actually bill as rather than making people do the arithmetic.
  const { data: billingSettings } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: getBillingSettings,
    staleTime: 5 * 60 * 1000,
  });

  const isPending = startMutation.isPending || createMutation.isPending || updateMutation.isPending;

  const parsedDuration = parseDurationToMinutes(durationText);
  const isDurationValid = parsedDuration !== null && parsedDuration > 0;
  // Typed something that is not a duration at all — refuse rather than guess.
  const isDurationUnparseable = durationText.trim() !== '' && parsedDuration === null;
  const roundingMinutes = billingSettings?.time_rounding_minutes ?? 6;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!matterId) return;

    if (mode === 'start') {
      startMutation.mutate({
        matter_id: matterId,
        description: description || undefined,
        activity_category: category || undefined,
        billable,
      });
    } else if (mode === 'edit' && entry) {
      if (isDurationUnparseable) return;
      // No positive duration typed — an empty field, or a sub-minute entry that
      // renders as "0m". Keep what the entry already had rather than wiping it.
      const durationSeconds = isDurationValid
        ? Math.round(parsedDuration * 60)
        : (entry.duration_seconds || undefined);
      updateMutation.mutate({
        matter_id: matterId,
        description: description || undefined,
        duration_seconds: durationSeconds,
        entry_date: entryDate,
        activity_category: category || undefined,
        billable,
        ...(isAdmin && rateCents !== '' ? { rate_cents: rateCents } : {}),
      });
    } else if (mode === 'manual') {
      handleManualCreate();
    }
  };

  const handleManualCreate = () => {
    if (!matterId || !isDurationValid) return;
    createMutation.mutate({
      matter_id: matterId,
      description: description || undefined,
      duration_seconds: Math.round(parsedDuration! * 60),
      entry_date: entryDate,
      activity_category: category || undefined,
      billable,
      ...(isAdmin && rateCents !== '' ? { rate_cents: rateCents } : {}),
    });
  };

  const selectedMatter = selectedMatterInfo?.id === matterId
    ? selectedMatterInfo
    : initialMatter && initialMatter.id === matterId
      ? { id: initialMatter.id, title: initialMatter.title }
      : null;

  const dialogTitle = mode === 'start'
    ? 'Start Timer'
    : mode === 'manual'
      ? 'Log Time Entry'
      : 'Edit Time Entry';

  return (
    <Dialog onClose={onClose} ariaLabel={dialogTitle} zIndex={9999}>
      <div style={{
        background: 'var(--card)', borderRadius: 'var(--radius)', width: 480, maxWidth: '95vw',
        boxShadow: '0 16px 48px rgba(0,0,0,0.2)', maxHeight: '85vh', overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-light)',
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            {dialogTitle}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.25rem' }}>
          {/* Matter selection */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
              Matter *
            </label>
            {selectedMatter ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)', fontSize: '0.85rem',
              }}>
                <span>{selectedMatter.title}</span>
                <button type="button" onClick={() => { setMatterId(null); setSelectedMatterInfo(null); }} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.75rem',
                }}>Change</button>
              </div>
            ) : matterId !== null && isMatterLoading ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)', fontSize: '0.85rem', color: 'var(--text-muted)',
              }}>
                <span>Loading matter #{matterId}...</span>
                <button type="button" onClick={() => { setMatterId(null); setSelectedMatterInfo(null); }} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.75rem',
                }}>Change</button>
              </div>
            ) : matterId !== null && (isMatterError || !initialMatter) ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.75rem', border: '1px solid var(--danger-border, #fca5a5)', borderRadius: 'var(--radius-sm)',
                background: 'var(--danger-bg, #fef2f2)', fontSize: '0.85rem', color: 'var(--danger, #dc2626)',
              }}>
                <span>Failed to load matter #{matterId}</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => { void refetchMatter(); }} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #dc2626)', fontSize: '0.75rem', textDecoration: 'underline',
                  }}>Retry</button>
                  <button type="button" onClick={() => { setMatterId(null); setSelectedMatterInfo(null); }} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.75rem',
                  }}>Change</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    value={matterSearch}
                    onChange={(e) => { setMatterSearch(e.target.value); setShowDropdown(true); }}
                    onFocus={() => { clearTimeout(blurTimeoutRef.current); setShowDropdown(true); }}
                    onBlur={() => { blurTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); }}
                    placeholder="Search matters... (type 2+ characters)"
                    autoFocus={!selectedMatter && matterId === null}
                    style={{
                      width: '100%', padding: '0.5rem 0.5rem 0.5rem 2rem',
                      border: '1px solid var(--border)', borderRadius: showDropdown && matters.length > 0 ? 'var(--radius-sm) var(--radius-sm) 0 0' : 'var(--radius-sm)',
                      fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                {showDropdown && matters.length > 0 && (
                  <div style={{
                    border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
                    maxHeight: 200, overflowY: 'auto',
                  }}>
                    {matters.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setMatterId(m.id); setSelectedMatterInfo({ id: m.id, title: m.title }); setMatterSearch(''); setShowDropdown(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '0.45rem 0.75rem', border: 'none', background: 'var(--card)',
                          cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text)',
                          borderBottom: '1px solid var(--border-light)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--card)'}
                      >
                        <div style={{ fontWeight: 500 }}>{m.title}</div>
                        {m.contact_name && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.contact_name}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {showDropdown && matterSearch.length >= 2 && debouncedSearch !== matterSearch && (
                  <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Searching...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="timer-description" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
              Description
            </label>
            <input
              id="timer-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
              autoFocus={Boolean(selectedMatter || matterId !== null)}
              style={{
                width: '100%', padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Category + Billable row */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="timer-category" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                Category
              </label>
              <select
                id="timer-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">-- None --</option>
                {ACTIVITY_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
                Billable
              </label>
            </div>
          </div>

          {/* Manual/edit fields */}
          {(mode === 'start' || mode === 'edit' || mode === 'manual') && (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="timer-duration" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                    Duration
                  </label>
                  <input
                    id="timer-duration"
                    type="text"
                    inputMode="text"
                    required={mode === 'manual'}
                    value={durationText}
                    onChange={(e) => setDurationText(e.target.value)}
                    placeholder="e.g. 3h 20m, 45m, 1:30, 90"
                    aria-invalid={isDurationUnparseable || undefined}
                    style={{
                      width: '100%', padding: '0.5rem 0.75rem',
                      border: `1px solid ${isDurationUnparseable ? 'var(--danger, #dc2626)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ minHeight: '1.1rem', marginTop: '0.3rem', fontSize: '0.75rem' }}>
                    {isDurationUnparseable && (
                      <span style={{ color: 'var(--danger, #dc2626)' }}>
                        That is not a duration — try 3h 20m, 45m, or 1:30.
                      </span>
                    )}
                    {isDurationValid && !isDurationUnparseable && (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {billableSummary(parsedDuration, roundingMinutes)}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="timer-date" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                    Date
                  </label>
                  <input
                    id="timer-date"
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    style={{
                      width: '100%', padding: '0.5rem 0.75rem',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              {isAdmin && (
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="timer-rate" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                    Rate ($/hr)
                  </label>
                  <input
                    id="timer-rate"
                    type="number"
                    min={0}
                    step={0.01}
                    value={rateCents !== '' ? rateCents / 100 : ''}
                    onChange={(e) => setRateCents(e.target.value ? Math.round(Number(e.target.value) * 100) : '')}
                    placeholder="Default user rate"
                    style={{
                      width: '100%', padding: '0.5rem 0.75rem',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem', background: 'var(--card)', color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', background: 'var(--card)',
                color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Cancel
            </button>
            {mode === 'start' ? (
              <>
                <button
                  type="button"
                  onClick={handleManualCreate}
                  disabled={!matterId || !isDurationValid || isPending}
                  style={{
                    padding: '0.5rem 1rem', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)', background: 'var(--card)',
                    color: 'var(--text)', cursor: 'pointer', fontSize: '0.85rem',
                    opacity: (!matterId || !isDurationValid || isPending) ? 0.5 : 1,
                    display: isDurationValid ? 'inline-block' : 'none',
                  }}
                >
                  Log Manual
                </button>
                <button
                  type="submit"
                  formNoValidate
                  disabled={!matterId || isPending}
                  style={{
                    padding: '0.5rem 1rem', border: 'none',
                    borderRadius: 'var(--radius-sm)', background: '#16a34a',
                    color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                    opacity: (!matterId || isPending) ? 0.5 : 1,
                  }}
                >
                  {isPending ? 'Starting...' : 'Start Timer'}
                </button>
              </>
            ) : mode === 'manual' ? (
              <button
                type="submit"
                disabled={!matterId || !isDurationValid || isPending}
                style={{
                  padding: '0.5rem 1rem', border: 'none',
                  borderRadius: 'var(--radius-sm)', background: 'var(--primary)',
                  color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                  opacity: (!matterId || !isDurationValid || isPending) ? 0.5 : 1,
                }}
              >
                {isPending ? 'Logging...' : 'Log Time'}
              </button>
            ) : (
              <button
                type="submit"
                disabled={!matterId || isDurationUnparseable || isPending}
                style={{
                  padding: '0.5rem 1rem', border: 'none',
                  borderRadius: 'var(--radius-sm)', background: 'var(--primary)',
                  color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                  opacity: (!matterId || isDurationUnparseable || isPending) ? 0.5 : 1,
                }}
              >
                {isPending ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Dialog>
  );
}
